import React, { useEffect, useState } from 'react';
import { Button, Card, Divider, Group, Radio, Stack, Stepper, Text, Title, Grid, Transition } from '@mantine/core';
import { ArrowRight, Check, Target } from 'lucide-react';
import { radarAxes } from '../data/protocolData';
interface BuildWizardProps {
  onOutcomesPrioritized: (outcomes: string[]) => void;
}
export function BuildWizard({
  onOutcomesPrioritized
}: BuildWizardProps) {
  const [active, setActive] = useState(0);
  const [selections, setSelections] = useState<string[]>([]);
  const [transitioning, setTransitioning] = useState(false);
  const [contentVisible, setContentVisible] = useState(true);
  const handleSelection = (value: string) => {
    setSelections({
      ...selections,
      [active]: value
    });
  };
  const nextStep = () => {
    setTransitioning(true);
    setContentVisible(false);
  };
  useEffect(() => {
    if (transitioning && !contentVisible) {
      const timer = setTimeout(() => {
        if (active < 2) {
          setActive(active + 1);
        } else {
          onOutcomesPrioritized(Object.values(selections));
        }
        setContentVisible(true);
        setTransitioning(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [transitioning, contentVisible, active, selections, onOutcomesPrioritized]);
  const prevStep = () => {
    setTransitioning(true);
    setContentVisible(false);
  };
  const isLastStep = active === 2;
  const canProceed = selections[active] !== undefined;
  // Don't filter out already selected options, just show all options
  return <Card shadow="sm" p="xl" radius="md" className="bg-white" data-id="element-103">
      <Stepper active={active} onStepClick={setActive} className="mb-8" data-id="element-104">
        <Stepper.Step label="First Priority" icon={<Target size={18} data-id="element-106" />} data-id="element-105" />
        <Stepper.Step label="Second Priority" icon={<Target size={18} data-id="element-108" />} data-id="element-107" />
        <Stepper.Step label="Third Priority" icon={<Target size={18} data-id="element-110" />} data-id="element-109" />
      </Stepper>
      <Transition mounted={contentVisible} transition="fade" duration={300} timingFunction="ease" data-id="element-111">
        {styles => <div style={styles} className="mb-8" data-id="element-112">
            <Title order={3} className="mb-4 text-slate-800" data-id="element-113">
              Select your{' '}
              {active === 0 ? 'first' : active === 1 ? 'second' : 'third'} most
              important health outcome
            </Title>
            <Divider className="mb-4" variant="transparent" data-id="element-114" />
            <Text className="mb-8 text-slate-500 italic" data-id="element-115">
              Choose the health dimension that matters most to you
              {active === 0 ? '' : ' after your previous selection(s)'}.
            </Text>
            <Divider className="mb-8" variant="transparent" data-id="element-116" />
            <Radio.Group value={selections[active]} onChange={handleSelection} data-id="element-117">
              <Grid data-id="element-118">
                {radarAxes.map(option => <Grid.Col span={6} key={option.id} data-id="element-119">
                    <Radio value={option.id} label={<div data-id="element-121">
                          <Text size="md" className="text-slate-900 font-medium" data-id="element-122">
                            {option.label}
                          </Text>
                          <Text size="sm" className="text-slate-600" data-id="element-123">
                            {option.description}
                          </Text>
                          <Text size="xs" className="text-slate-500 italic" data-id="element-124">
                            Metrics: {option.metrics}
                          </Text>
                        </div>} disabled={Object.values(selections).includes(option.id)} className="mb-6" classNames={{
                body: 'flex items-start',
                inner: 'mt-1'
              }} data-id="element-120" />
                  </Grid.Col>)}
              </Grid>
            </Radio.Group>
          </div>}
      </Transition>
      <Group justify="flex-end" mt="xl" data-id="element-125">
        <Button variant="light" onClick={prevStep} disabled={active === 0 || transitioning} data-id="element-126">
          Back
        </Button>
        <Button onClick={nextStep} disabled={!canProceed || transitioning} rightSection={isLastStep ? <Check size={16} data-id="element-128" /> : <ArrowRight size={16} data-id="element-129" />} className="bg-emerald-600 hover:bg-emerald-700" data-id="element-127">
          {transitioning && isLastStep ? 'Finding Plans...' : isLastStep ? 'Find Compatible Plans' : 'Next Step'}
        </Button>
      </Group>
    </Card>;
}